import {
  getDefaultSetupSettings,
  type UserAlertSettings,
  type UserAlertSettingsAPI,
} from './definitions';
import {
  toUserAlertSettings,
  toUserAlertSettingsAPI,
} from './userAlertSettingsTransformer';
import { gql } from '@apollo/client';

import graphQLRequester from '../../../request/GraphQLRequester';

export class GraphQLUserAlertSettingsRequester {
  public setupSettings = (
    settingsToSetup: UserAlertSettings[]
  ): Promise<UserAlertSettings[]> =>
    graphQLRequester
      .mutate<{ setupUserNotificationSettings: UserAlertSettingsAPI[] }>({
        mutation: gql`
          mutation SetupUserNotificationSettings(
            $settingsToSetup: [InsertUserNotificationSettings]
          ) {
            setupUserNotificationSettings(input: $settingsToSetup) {
              id
              type
              active
              settings_json {
                ... on RoadSpeedLimitSettings {
                  duration_above_limit_in_millis
                  severity
                  vehicle_group_ids
                }
                ... on VehicleSpeedLimitSettings {
                  duration_above_limit_in_millis
                  vehicle_group_ids
                }
                ... on StopIdleTimeSettings {
                  duration_idle_in_millis
                  vehicle_group_ids
                }
              }
            }
          }
        `,
        variables: {
          settingsToSetup: settingsToSetup.map(toUserAlertSettingsAPI),
        },
      })
      .then((result) =>
        result.setupUserNotificationSettings.map(toUserAlertSettings)
      );

  public insertSettings = (
    newSettings: UserAlertSettings
  ): Promise<UserAlertSettings> =>
    graphQLRequester
      .mutate<{ insertUserNotificationSettings: UserAlertSettingsAPI }>({
        mutation: gql`
          mutation InsertUserNotificationSettings(
            $settingsToInsert: InsertUserNotificationSettings
          ) {
            insertUserNotificationSettings(input: $settingsToInsert) {
              type
              active
              settings_json {
                ... on RoadSpeedLimitSettings {
                  duration_above_limit_in_millis
                  severity
                  vehicle_group_ids
                }
                ... on VehicleSpeedLimitSettings {
                  duration_above_limit_in_millis
                  vehicle_group_ids
                }
                ... on StopIdleTimeSettings {
                  duration_idle_in_millis
                  vehicle_group_ids
                }
              }
            }
          }
        `,
        variables: {
          settingsToInsert: toUserAlertSettingsAPI(newSettings),
        },
      })
      .then((result) =>
        toUserAlertSettings(result.insertUserNotificationSettings)
      );

  public getSettings = (): Promise<UserAlertSettings[]> =>
    graphQLRequester
      .query<{ settings: UserAlertSettingsAPI[] }>({
        query: gql`
          query settings {
            settings {
              id
              type
              active
              settings_json {
                ... on RoadSpeedLimitSettings {
                  duration_above_limit_in_millis
                  severity
                  vehicle_group_ids
                }
                ... on VehicleSpeedLimitSettings {
                  duration_above_limit_in_millis
                  vehicle_group_ids
                }
                ... on StopIdleTimeSettings {
                  duration_idle_in_millis
                  vehicle_group_ids
                }
              }
            }
          }
        `,
      })
      .then((result) => {
        const defaultUserAlertSettings = getDefaultSetupSettings();
        const userAlertSettings = result.settings.map(toUserAlertSettings);

        return defaultUserAlertSettings.map((defaultSetting) => {
          const savedSetting = userAlertSettings.find(
            (setting) => setting.type === defaultSetting.type
          );
          return savedSetting || defaultSetting;
        });
      });

  public updateSettings = (
    userAlertSettings: UserAlertSettings
  ): Promise<UserAlertSettings> =>
    graphQLRequester
      .mutate<{ updateUserNotificationSettings: UserAlertSettingsAPI }>({
        mutation: gql`
          mutation UpdateUserNotificationSettings(
            $settingsToUpdate: UpdateUserNotificationSettings
          ) {
            updateUserNotificationSettings(input: $settingsToUpdate) {
              id
              type
              active
              settings_json {
                ... on RoadSpeedLimitSettings {
                  duration_above_limit_in_millis
                  severity
                  vehicle_group_ids
                }
                ... on VehicleSpeedLimitSettings {
                  duration_above_limit_in_millis
                  vehicle_group_ids
                }
                ... on StopIdleTimeSettings {
                  duration_idle_in_millis
                  vehicle_group_ids
                }
              }
            }
          }
        `,
        variables: {
          settingsToUpdate: toUserAlertSettingsAPI(userAlertSettings),
        },
      })
      .then((graphQlResponse) =>
        toUserAlertSettings(graphQlResponse.updateUserNotificationSettings)
      );
}

export default GraphQLUserAlertSettingsRequester;
